import * as React from "react";

import { cn } from "utils/cn";
import { useStableId } from "./useStableId";

// SAP's mark: a hub inside an orbit with two deployments on it, the "control plane" idea from the
// sign-in panel. The orbit's dash pattern leaves a gap around each satellite node.
const ORBIT_DASH = "28.06 6.5";
const ORBIT_OFFSET = 4.43;

export type LogoMarkProps = React.SVGProps<SVGSVGElement>;

export const LogoMark: React.FC<LogoMarkProps> = ({ className, ...props }) => {
  const id = useStableId("logo");
  return (
    <svg
      data-ui="logo-mark"
      viewBox="0 0 32 32"
      aria-hidden="true"
      className={cn("h-7 w-7 shrink-0", className)}
      {...props}
    >
      <defs>
        <linearGradient
          id={`${id}-orbit`}
          x1="5"
          y1="27"
          x2="27"
          y2="5"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#0B6FE1" />
          <stop offset="100%" stopColor="#1CC7B1" />
        </linearGradient>
        <linearGradient
          id={`${id}-hub`}
          x1="12"
          y1="20"
          x2="20"
          y2="12"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="#0B6FE1" />
          <stop offset="100%" stopColor="#1F9BFF" />
        </linearGradient>
      </defs>
      <circle
        cx="16"
        cy="16"
        r="11"
        fill="none"
        stroke={`url(#${id}-orbit)`}
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeDasharray={ORBIT_DASH}
        strokeDashoffset={ORBIT_OFFSET}
      />
      <circle cx="16" cy="16" r="4.6" fill={`url(#${id}-hub)`} />
      <circle cx="24.43" cy="8.93" r="2.7" fill="#1CC7B1" />
      <circle cx="7.57" cy="23.07" r="2.7" fill="#0B6FE1" />
    </svg>
  );
};

export interface LogoProps {
  className?: string;
  /** Hide the wordmark and show only the mark (collapsed sidebar). */
  compact?: boolean;
}

/** Mark + "Super Admin / Meta16 Labs" wordmark. Text colors follow the surrounding text color. */
export const Logo: React.FC<LogoProps> = ({ className, compact = false }) => (
  <span data-ui="logo" className={cn("inline-flex items-center gap-2.5", className)}>
    <LogoMark />
    {!compact && (
      <span className="flex flex-col leading-none">
        <span className="text-[15px] font-semibold tracking-tight">Super Admin</span>
        <span className="mt-1 text-xs opacity-60">Meta16 Labs</span>
      </span>
    )}
  </span>
);
